// src/components/layout/PageHeader/index.jsx
import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, notification } from "antd";
import {
  PlusSquareOutlined,
  PlusOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CopyFilled,
} from "@ant-design/icons";
import ExportModal from "../../Modals/ExportModal";
import AddOrganizationModal from "../../Modals/AddOrganizationModal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LuArrowLeft, LuShare2 } from "react-icons/lu";
import CreateFormModal from "../../Modals/CreateFormModal";
import DeleteModal from "../../Modals/DeleteModal";
import SubmitFormModal from "../../Modals/SubmitFormModal";
import {
  DeleteIcon,
  EditIcon,
  ExportIcon,
  PlusIcon,
  PrintIcon,
} from "../../../assets/svgs/icons";
import AssignInterpreterModal from "../../Modals/AssignInterpreterModal";
import { FileVideo } from "lucide-react";
import AddInterpreterModal from "../../Modals/AddInterpreterModal";
import AddPanelUserModal from "../../Modals/AddPanelUserModal";
import ImportPanelUsersModal from "../../Modals/ImportPanelUsersModal";
import CreateCampaignModal from "../../Modals/CreateCampaignModal";
import ActivateDateWarningModal from "../../Modals/ActivateDateWarningModal";
import CopyFormEnvironmentModal from "../../Modals/CopyFormEnvironmentModal";
import {
  useDeleteCampaignMutation,
  useDeleteFormMutation,
  useGetCampaignByIdQuery,
  useGetFormDetailsQuery,
  useGetFormResponsesQuery,
  useGetQuestionByIdQuery,
  useUpdateDatesActivateMutation,
  useActivateFormMutation,
  useDeactivateFormMutation,
  useCopyFormToEnvironmentMutation,
  useGetPanelUsersQuery,
} from "../../../store/api/apiSlice";
import { exportPanelUsersToCsv } from "../../../pages/admin/PanelUser/helpers";
import { useSelector } from "react-redux";
import { selectCurrentRole } from "../../../store/auth/authSlice";
import ExportReportModal from "../../Modals/ExportReportModal";
import WidgetModal from "../../Modals/WidgetModal";
import { ENVIRONMENT_TARGETS } from "../../../config/environment";
import { getCampaignStatusFromForms } from "../../../utils/campaigns";
import { normalizeFormStatus } from "../../../utils/formTabs";

const pageConfigs = {
  "/admin/forms/archived": {
    title: "Archived Forms",
    description:
      "Manage your archived forms, add or remove forms from the list.",
    breadcrumb: {
      parent: "Forms",
      current: "Archived Forms",
    },
    actions: [],
  },
  "/admin/templates": {
    title: null,
    description: null,
    actions: [],
  },
  "/admin/enquiries": {
    title: "Enquiries",
    description: "View and manage contact and demo request submissions.",
    actions: [],
  },
  "/admin/organisations": {
    title: "Manage Organisations",
    description:
      "View and manage organisations, track form submissions, and monitor activity.",
    actions: [
      {
        key: "orgnization",
        label: "Add organisation",
        icon: <PlusSquareOutlined />,
        type: "primary",
      },
    ],
  },
  "organisations-details": {
    breadcrumb: {
      parent: "Organisations",
      current: "Organisation Details",
    },
    actions: [],
  },
  "/admin/forms": {
    title: "Manage Forms",
    description: "Create, manage and track  forms for compaings",
    actions: [
      {
        key: "archived",
        type: "primary",
        label: "Archived Forms",
        className:
          "bg-gray-100 border-0 text-gray-600 px-6 py-3 rounded-xl text-sm font-medium",
      },
      {
        key: "createForm",
        label: "Create Form",
        icon: <PlusSquareOutlined />,
        type: "primary",
      },
    ],
  },
  "/admin/template-form": {
    title: "Manage Forms",
    description: "Create, manage and track forms for campaigns",
    actions: [
      {
        key: "share",
        icon: <LuShare2 className="text-gray-600" size={20} />,
        type: "icon",
      },
      {
        key: "delete",
        icon: <DeleteIcon className="text-red-500" size={20} />,
        type: "icon",
      },
      {
        key: "edit",
        icon: <EditIcon className="text-gray-600" size={20} />,
        type: "icon",
      },
      {
        key: "view",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        ),
        type: "icon",
        className: "text-gray-600 cursor-pointer",
      },
      {
        key: "createForm",
        label: "Create Form",
        icon: <PlusSquareOutlined />,
        type: "primary",
        className:
          "bg-[#18181B] border-0 text-white px-6 py-3 rounded-xl text-sm font-medium",
      },
    ],
  },
  "form-details": {
    breadcrumb: {
      parent: "Manage Form",
      current: "Form Details",
    },
    actions: [
      {
        key: "share",
        icon: <LuShare2 className="text-gray-600" size={20} />,
        type: "icon",
      },
      {
        key: "delete",
        icon: <DeleteIcon className="text-red-500" size={20} />,
        type: "icon",
      },
      {
        key: "edit",
        icon: <EditIcon className="text-gray-600" size={20} />,
        type: "icon",
      },
      {
        key: "submit",
        label: "Start Form",
        type: "button",
        className:
          "bg-emerald-500 border-0 text-white px-6 py-3 rounded-xl text-sm font-medium flex items-center gap-2",
      },
    ],
  },
  "form-responses": (userRole, totalResponses = 0) => ({
    breadcrumb: {
      parent: "Manage Form",
      current: "Form Details",
    },
    actions:
      userRole === "admin"
        ? [
            {
              key: "share",
              icon: <LuShare2 className="text-gray-600" size={20} />,
              type: "icon",
            },
            {
              key: "delete",
              icon: <DeleteIcon className="text-red-500" size={20} />,
              type: "icon",
            },
            {
              key: "edit",
              icon: <EditIcon className="text-gray-600" size={20} />,
              type: "icon",
            },
            {
              key: "submitOrganization",
              label:
                totalResponses === 0
                  ? "Submit to Organisation"
                  : "Submit to Organisation",
              type: "button",
              disabled: totalResponses === 0,
              className:
                totalResponses === 0
                  ? "bg-gray-400 border-0 text-white px-6 py-3 rounded-xl text-sm font-medium flex items-center gap-2 cursor-not-allowed"
                  : "bg-emerald-500 border-0 text-white px-6 py-3 rounded-xl text-sm font-medium flex items-center gap-2",
            },
          ]
        : [
            {
              key: "share",
              icon: <LuShare2 className="text-gray-600" size={20} />,
              type: "icon",
            },
          ],
  }),
  "question-details": (userRole) => ({
    breadcrumb: {
      parent: "Manage Form",
      middle: "Form Details",
      current: "Question Details",
    },
    actions:
      userRole === "admin"
        ? [
            {
              key: "delete",
              icon: <DeleteIcon className="text-red-500" size={20} />,
              type: "icon",
            },
            {
              key: "edit",
              icon: <EditIcon className="text-gray-600" size={20} />,
              type: "icon",
            },
            {
              key: "assign",
              label: "Assign Interpreter",
              type: "button",
              className:
                "bg-[#18181B] border-0 text-white px-6 py-3 rounded-xl text-sm font-medium",
            },
          ]
        : [],
  }),
  "add-question": {
    title: null,
    description: null,
    actions: [],
  },
  "edit-question": {
    title: null,
    description: null,
    actions: [],
  },
  "/admin/campaigns": {
    title: "Manage Campaign",
    description: "Create and manage campaigns, link Forms, and track progress.",
    actions: [
      {
        key: "add",
        label: (
          <span className="flex items-center gap-2  text-white">
            <PlusOutlined /> Create Campaign
          </span>
        ),
        type: "primary",

        className:
          " border-0 text-white px-6 py-3 rounded-xl text-sm font-medium ",
      },
    ],
  },
  "campaign-details": (userRole) => ({
    breadcrumb: {
      parent: "Manage Campaigns",
      current: "Campaign Details",
    },
    actions:
      userRole === "admin"
        ? [
            {
              key: "share",
              icon: <LuShare2 className="text-gray-600" size={20} />,
              type: "icon",
            },
            {
              key: "delete",
              icon: <DeleteIcon className="text-red-500" size={20} />,
              type: "icon",
            },
            {
              key: "edit",
              icon: <EditIcon className="text-gray-600" size={20} />,
              type: "icon",
            },
          ]
        : [
            {
              key: "share",
              icon: <LuShare2 className="text-gray-600" size={20} />,
              type: "icon",
            },
          ],
  }),
  "/admin/interpreter": {
    title: "Manage Interpreters",
    description:
      "Assign interpreters, track their progress, and manage interpreter profiles.",
    actions: [
      {
        key: "manageAssignments",
        label: (
          <span>
            <FileVideo className="text-sm relative top-0.5 mr-1" size={17} />{" "}
            Manage Assignments
          </span>
        ),
        type: "secondary",
        className:
          "bg-gray-100 border-0 text-gray-600 px-6 py-3 rounded-xl text-sm font-medium",
      },
      {
        key: "addInterpreters",
        label: (
          <span className="flex items-center gap-2">
            <span className="relative top-1 mr-2">
              <PlusIcon color={"#fff"} />
            </span>{" "}
            Add Interpreters
          </span>
        ),
        type: "primary",
        className:
          "bg-[#18181B] border-0 text-white px-6 py-3 rounded-xl text-sm font-medium",
      },
    ],
  },
  "/admin/panel-user": {
    title: "Manage Panel Users",
    description:
      "Manage panel user profiles, track activity, and update account status.",
    actions: [
      {
        key: "exportPanelUsers",
        label: "Export Panel Users",
        type: "default",
        className:
          "border border-gray-200 text-gray-700 px-6 py-3 rounded-xl text-sm font-medium",
      },
      {
        key: "importPanelUsers",
        label: "Import Panel Users",
        type: "default",
        className:
          "border border-gray-200 text-gray-700 px-6 py-3 rounded-xl text-sm font-medium",
      },
      {
        key: "addPanelUsers",
        label: (
          <span className="flex items-center gap-2">
            <span className="relative top-1 mr-2">
              <PlusIcon color={"#fff"} />
            </span>{" "}
            Add Panel User
          </span>
        ),
        type: "primary",
        className:
          "bg-[#18181B] border-0 text-white px-6 py-3 rounded-xl text-sm font-medium",
      },
    ],
  },
  "manage-assignments": {
    breadcrumb: {
      parent: "Manage Interpreters",
      current: "Manage Assignments",
    },
    actions: [], 
  },
  "/interpreter/dashboard": {
    title: "Dashboard Analytics",
    description:
      "Gain insights into the performance and responses of your Forms. Track interpreter activities, Form statuses, and completion metrics.",
    actions: [
      {
        key: "print",
        label: "Print Analytics",
        icon: <PrintIcon />,
        type: "secondary",
      },
    ],
  },
  "/organisation/dashboard": {
    title: "Dashboard Analytics",
    description:
      "Gain insights into the performance and responses of your Forms. Track interpreter activities, Form statuses, and completion metrics.",
    actions: [
      {
        key: "export",
        label: "Export Report",
        icon: <ExportIcon />,
        type: "primary",
      },
    ],
  },
  "/organisation/campaigns": {
    title: "Manage Campaign",
    description: "Create and manage campaigns, link Forms, and track progress.",
    actions: [
      {
        key: "export",
        label: (
          <span className="flex items-center gap-2 pt-2 text-white">
            <ExportIcon className="  " />
            <span className="relative ml-2 -top-2">Export Report </span>
          </span>
        ),
        type: "primary",

        className:
          " border-0 text-white px-6 py-3 rounded-xl text-sm font-medium ",
      },
    ],
  },
  "/organisation/forms": {
    title: "Manage Forms ",
    description: "Create, manage and track  forms for compaings",
    actions: [
      {
        key: "ExportReportOrganisation",
        label: "Export Report",
        icon: <ExportIcon />,
        type: "primary",
      },
    ],
  },
};

const DEFAULT_PAGE_CONFIG = {
  title: "Page Not Found",
  description: "The requested page could not be found.",
  actions: [],
};

const resolvePageConfig = (pathname, search = "") => {
  if (/\/admin\/question(\/|$)/.test(pathname)) {
    return { title: null, description: null, actions: [] };
  }
  if (pathname.startsWith("/admin/template-form/")) {
    return pageConfigs["/admin/template-form"];
  }
  if (pathname === "/admin/templates") {
    return pageConfigs["/admin/templates"];
  }
  if (pathname === "/admin/forms/archived") {
    return pageConfigs["/admin/forms/archived"];
  }
  if (pathname.includes("/organisations/") && pathname !== "/admin/organisations") {
    return pageConfigs["organisations-details"];
  }
  if (pathname.includes("/interpreter/assignments")) {
    return pageConfigs["manage-assignments"];
  }
  if (pathname.includes("/campaigns/") && pathname !== "/admin/campaigns") {
    return pageConfigs["campaign-details"]("admin");
  }
  if (pathname.includes("/questions/add")) {
    return pageConfigs["add-question"];
  }
  if (pathname.includes("/questions/edit")) {
    return pageConfigs["edit-question"];
  }
  if (pathname.includes("/questions/details")) {
    return pageConfigs["question-details"];
  }
  if (pathname.includes("/forms/") && search.includes("tab=responses")) {
    return pageConfigs["form-responses"]("admin", 0);
  }
  if (
    pathname.includes("/forms/") &&
    !pathname.includes("/questions/") &&
    pathname !== "/admin/forms"
  ) {
    return pageConfigs["form-details"];
  }

  return pageConfigs[pathname] || DEFAULT_PAGE_CONFIG;
};

const extractFormId = () => {
  const path = window.location.pathname; 
  const segments = path.split("/"); 
  const formIdIndex = segments.indexOf("forms") + 1;
  return segments[formIdIndex];
};

const extractTemplateFormId = () => {
  const path = window.location.pathname;
  const match = path.match(/\/admin\/template-form\/([^/]+)$/);
  return match ? match[1] : null;
};

const PageHeader = ({ collapsed, setCollapsed, showToggle }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const navigate = useNavigate();
  const [deleteForm] = useDeleteFormMutation();
  const [updateDatesActivate] = useUpdateDatesActivateMutation();
  const [activateForm] = useActivateFormMutation();
  const [deactivateForm] = useDeactivateFormMutation();
  const { state: locationState } = useLocation();
  const params = useParams();
  const dlocation = useLocation();
  const pathSegments = dlocation.pathname.split("/");

  const userRole = useSelector(selectCurrentRole);

  const [isStarting, setIsStarting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [addOrgModalVisible, setAddOrgModalVisible] = useState(false);
  const [createFormModalVisible, setCreateFormModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [addInterpreterModalVisible, setAddInterpreterModalVisible] =
    useState(false);
  const [addPanelUserModalVisible, setAddPanelUserModalVisible] =
    useState(false);
  const [importPanelUsersModalVisible, setImportPanelUsersModalVisible] =
    useState(false);
  const [submitModalVisible, setSubmitModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [widgetModalVisible, setWidgetModalVisible] = useState(false);
  const [createCampaignModalVisible, setCreateCampaignModalVisible] =
    useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [copyFormModalVisible, setCopyFormModalVisible] = useState(false);
  const [config, setConfig] = useState(() =>
    resolvePageConfig(location.pathname, location.search),
  );
  const [showActivateDateModal, setShowActivateDateModal] = useState(false);

  const [deleteCampaign] = useDeleteCampaignMutation();
  const [copyFormToEnvironment, { isLoading: isCopyingForm }] =
    useCopyFormToEnvironmentMutation();
  const { data: campaignData } = useGetCampaignByIdQuery(
    location.pathname.split("/campaigns/")[1],
    {
      skip: !location.pathname.includes("/campaigns/"),
    },
  );
  const formId = extractFormId();
  const { data: formDetailsData, isLoading: formDetailsLoading } =
    useGetFormDetailsQuery(formId, {
      skip: !formId,
    });
  const formData = locationState || formDetailsData?.data;
  const { data: responsesData } = useGetFormResponsesQuery(formId, {
    skip: !formId,
  });
  const totalResponses = responsesData?.data?.length || 0;

  const isPanelUserPage = location.pathname.includes("/panel-user");
  const { data: panelUsersData } = useGetPanelUsersQuery(
    {},
    { skip: !isPanelUserPage },
  );

  const [formStatus, setFormStatus] = useState(formData?.status);
  const [localFormData, setLocalFormData] = useState(formData);

  const isDetailsPage = Boolean(config?.breadcrumb);
  const questionId =
    params.questionId ||
    (location.pathname.includes("/questions/")
      ? pathSegments[pathSegments.length - 1]
      : null);
  const { data: questionData } = useGetQuestionByIdQuery(questionId, {
    skip: !questionId || location.pathname === "/admin/video-catalog",
  });

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteForm(formId).unwrap();
      navigate("/admin/forms");
    } catch (error) {
    } finally {
      setIsDeleting(false);
      setDeleteModalVisible(false);
    }
  };

  const handleCampaignShare = () => {
    const campaignId = location.pathname.split("/campaigns/")[1];

    if (!campaignData?.data) {
      notification.error({
        message: "Error",
        description: "Campaign data not available",
      });

      return;
    }

    const forms = campaignData?.data?.forms || [];
    const campaignStatus = getCampaignStatusFromForms(forms);

    if (campaignStatus !== "active") {
      notification.warning({
        message: "Cannot Share",
        description: "This campaign has no active survey to share",
      });

      return;
    }

    if (!forms.length) {
      notification.error({
        message: "Error",
        description: "This campaign has no forms to share",
      });

      return;
    }

    const activeForm = forms.find(
      (form) => normalizeFormStatus(form?.status) === "active",
    );
    const formId = activeForm?._id || forms[0]._id;

    const campaignUrl = `${window.location.origin}/campaign-submissions/question/${campaignId}/${formId}/0`;

    navigator.clipboard
      .writeText(campaignUrl)
      .then(() => {
        notification.success({
          message: "Success",
          description: "Campaign link copied to clipboard",
        });
      })
      .catch((err) => {
        notification.error({
          message: "Failed to copy",
          description: "Could not copy the link to clipboard",
        });
      });
  };

  const handleCampaignDelete = () => {
    const campaignId = location.pathname.split("/campaigns/")[1];
    Modal.confirm({
      title: "Delete Campaign",
      content: "Are you sure you want to delete this campaign?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteCampaign(campaignId).unwrap();
       
          notification.success({
            message: "Success",
            description: "Campaign deleted successfully",
          });

          navigate("/admin/campaigns");
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.message || "Failed to delete campaign",
          });
        }
      },
    });
  };

  const handleSuccess = () => {
    setCreateCampaignModalVisible(false);
    setEditingCampaign(null);
  };

  const getQuestionDetailsConfig = useCallback(
    (questionData) => {
      const hasVideo = questionData?.data?.response?.some(
        (input) =>
          input.input_type === "record-video" || input.type === "record-video",
      );

      let actions = [];

      if (userRole === "admin") {
        actions = [
          {
            key: "delete",
            icon: <DeleteIcon className="text-red-500" size={20} />,
            type: "icon",
          },
          {
            key: "edit",
            icon: <EditIcon className="text-gray-600" size={20} />,
            type: "icon",
          },
          ...(hasVideo
            ? [
                {
                  key: "assign",
                  label: "Assign Interpreter",
                  type: "button",
                  className:
                    "bg-[#18181B] border-0 text-white px-6 py-3 rounded-xl text-sm font-medium",
                },
              ]
            : []),
        ];
      }

      return {
        breadcrumb: {
          parent: "Manage Form",
          middle: "Form Details",
          current: "Question Details",
        },
        actions: actions,
      };
    },
    [userRole],
  );

  const handleStartForm = async () => {
    try {
      const startUnix = localFormData?.start_date ?? formData?.start_date;
      const endUnix = localFormData?.end_date ?? formData?.end_date;

      if (!startUnix || !endUnix) {
        setShowActivateDateModal(true);
        return;
      }

      const toYMD = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
    
        return `${year}-${month}-${day}`;
      };

      const todayStr = toYMD(new Date());
      const startDate = new Date(startUnix);
      const endDate = new Date(endUnix);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        notification.error({
          message: "Invalid Dates",
          description: "Form has invalid start or end dates",
        });

        return;
      }

      const startStr = toYMD(startDate);
      const endStr = toYMD(endDate);
      const isWithinRange = todayStr >= startStr && todayStr <= endStr;

      if (!isWithinRange) {
        setShowActivateDateModal(true);
        return;
      }

      setIsStarting(true);
   
      await activateForm(formId).unwrap();

      setFormStatus("active");
      setLocalFormData((prev) => ({
        ...prev,
        status: "active",
      }));
    
      if (formData) {
        formData.status = "active";
      }

      notification.success({
        message: "Success",
        description: "Form started successfully",
      });
    } catch (error) {
      // Swallow; API layer also handles notifications
    } finally {
      setIsStarting(false);
    }
  };

  const toUnixSecondsString = (dateStr) => {
    if (!dateStr) return null;
  
    const date = new Date(`${dateStr}T00:00:00`);
  
    return String(Math.floor(date.getTime() / 1000));
  };

  const handleConfirmActivationWithDates = async ({ startDate, endDate }) => {
    try {
      setIsStarting(true);

    if (!startDate || !endDate) {
        notification.error({
          message: "Validation Error",
          description: "Please select both start and end dates",
        });

        return;
      }

      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);

      if (startDateObj >= endDateObj) {
        notification.error({
          message: "Validation Error",
          description: "End date must be after start date",
        });

        return;
      }

     const result = await updateDatesActivate({
        id: formId,
        startDate: toUnixSecondsString(startDate),
        endDate: toUnixSecondsString(endDate),
      }).unwrap();

      if (result && result.success) {
        setFormStatus("active");
        setLocalFormData((prevData) => ({
          ...prevData,
          status: "active",
          start_date: toUnixSecondsString(startDate),
          end_date: toUnixSecondsString(endDate),
        }));

        if (formData) {
          formData.status = "active";
          formData.start_date = toUnixSecondsString(startDate);
          formData.end_date = toUnixSecondsString(endDate);
        }
      }
    } catch (error) {
     if (error?.status === 400) {
        notification.error({
          message: "Validation Error",
          description:
            error?.data?.message || "Invalid date range or form data",
        });
      } else if (error?.status === 404) {
        notification.error({
          message: "Form Not Found",
          description: "The form you're trying to activate no longer exists",
        });
      } else if (error?.status === 500) {
        notification.error({
          message: "Server Error",
          description:
            "An error occurred on the server. Please try again later.",
        });
      } else if (error?.status === 401 || error?.status === 403) {
        notification.error({
          message: "Permission Denied",
          description: "You don't have permission to activate this form",
        });
      } else {
        notification.error({
          message: "Activation Failed",
          description:
            error?.data?.message ||
            "Failed to update form dates and activate. Please try again.",
        });
      }
    } finally {
      setIsStarting(false);
      setShowActivateDateModal(false);
    }
  };

  const toYMD = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getFormActions = (status) => {
    const baseActions = [
      {
        key: "view",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        ),
        type: "icon",
        className: "text-gray-600 cursor-pointer",
      },
      ...((userRole === "organization" || userRole === "admin") &&
      formData?.type === "enquiry"
        ? [
            {
              key: "widget",
              label: "Widget",
              type: "button",
              className:
                "bg-gray-100 border-0 text-gray-600 px-6 py-3 rounded-xl text-sm font-semibold",
            },
          ]
        : []),
      {
        key: "share",
        icon: <LuShare2 className="text-gray-600" size={20} />,
        type: "icon",
      },
      ...(userRole === "admin"
        ? [
            {
              key: "copy",
              icon: <CopyFilled className="text-gray-600" size={18} />,
              type: "icon",
              className: "text-gray-600 cursor-pointer",
            },
          ]
        : []),
      {
        key: "delete",
        icon: <DeleteIcon className="text-red-500" size={20} />,
        type: "icon",
      },
      {
        key: "edit",
        icon: <EditIcon className="text-gray-600" size={20} />,
        type: "icon",
      },
    ];

    if (userRole !== "admin") {
     return baseActions.filter(
        (action) =>
          action.key === "view" ||
          action.key === "share" ||
          action.key === "widget",
      );
    }

    if (status === "active") {
      baseActions.push({
        key: "end",
        label: "End Form",
        type: "button",
        className:
          "bg-red-500 border-0 text-white px-6 py-3 rounded-xl text-sm font-medium flex items-center gap-2",
      });
    } else {
      baseActions.push({
        key: "submit",
        label: "Start Form",
        type: "button",
        className:
          "bg-emerald-500 border-0 text-white px-6 py-3 rounded-xl text-sm font-medium flex items-center gap-2",
      });
    }

    return baseActions;
  };

  const handleEndForm = async () => {
    try {
      setIsStarting(true);
    
      await deactivateForm(formId).unwrap();

     setFormStatus("not_active");
      setLocalFormData((prevData) => ({
        ...prevData,
        status: "not_active",
      }));

      if (formData) {
        formData.status = "not_active";
      }

      notification.success({
        message: "Success",
        description: "Form ended successfully",
      });
    } catch (error) {
    } finally {
      setIsStarting(false);
    }
  };

  const handleShare = () => {
    const currentStatus = localFormData?.status || formStatus;
 
    if (currentStatus !== "active") {
      notification.warning({
        message: "Cannot Share",
        description: "Please activate the form before sharing",
      });

      return;
    }

    const formUrl = `${window.location.origin}/form-submissions/${formId}`;
  
    navigator.clipboard.writeText(formUrl).then(() => {
      notification.success({
        message: "Success",
        description: "Form link copied to clipboard",
      });
    });
  };

  const handleCopyForm = () => {
    setCopyFormModalVisible(true);
  };

  const handleCopyFormConfirm = async ({ title, environment, withResponses }) => {
    const trimmedTitle = String(title || "").trim();

    if (!trimmedTitle) {
      notification.error({
        message: "Title required",
        description: "Please enter a title for the new form.",
      });
      return;
    }

    if (!formId) {
      notification.error({
        message: "Error",
        description: "Form id is missing.",
      });
      return;
    }

    try {
      await copyFormToEnvironment({
        id: formId,
        title: trimmedTitle,
        environment,
        withResponses: Boolean(withResponses),
      }).unwrap();

      const envLabel =
        ENVIRONMENT_TARGETS[environment]?.label || environment;

      notification.success({
        message: "Form created",
        description: `New form “${trimmedTitle}” was created for ${envLabel}.`,
      });

      setCopyFormModalVisible(false);
    } catch (error) {
      notification.error({
        message: "Copy failed",
        description:
          error?.data?.message ||
          error?.error?.data?.message ||
          "Failed to create the form in the selected environment.",
      });
    }
  };

  const handleEdit = () => {
    try {
      setIsEditing(true);
      setEditingForm(formData);
      setCreateFormModalVisible(true);
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to open edit form. Please try again.",
      });
    }
  };

  const templateFormId = extractTemplateFormId();
  const { data: templateFormData } = useGetFormDetailsQuery(templateFormId, {
    skip: !templateFormId,
  });

  const handleTemplateFormEdit = () => {
    if (templateFormId) {
      if (templateFormData?.data) {
        setEditingForm(templateFormData.data);
        setCreateFormModalVisible(true);
      }
    }
  };

  const handleTemplateFormView = () => {
    const templateFormId = extractTemplateFormId();
 
    if (templateFormId) {
      const formUrl = `${window.location.origin}/admin/forms/${templateFormId}`;
      window.open(formUrl, "_blank");
    }
  };

  const handleActionClick = (action) => {
    const { key } = action;
    const isCampaignsPath = location.pathname.includes("/campaigns/");
    const isTemplatePath = location.pathname.startsWith(
      "/admin/template-form/",
    );

    const commonActions = {
      ExportReportOrganisation: () => setExportModalOpen(true),
      widget: () => setWidgetModalVisible(true),
      share: () => handleShare(),
      delete: () => setDeleteModalVisible(true),
      export: () => setExportModalVisible(true),
      archived: () => navigate("/admin/forms/archived"),
      archivedorganisation: () => navigate("/organisation/forms/archived"),
      orgnization: () => setAddOrgModalVisible(true),
      createForm: () => setCreateFormModalVisible(true),
      manageAssignments: () => navigate("/admin/interpreter/assignments"),
      addInterpreters: () => setAddInterpreterModalVisible(true),
      addPanelUsers: () => setAddPanelUserModalVisible(true),
      importPanelUsers: () => setImportPanelUsersModalVisible(true),
      exportPanelUsers: () => {
        const result = exportPanelUsersToCsv(panelUsersData);

        if (!result.ok) {
          notification.warning({
            message: "Nothing to export",
            description: "There are no panel users to export.",
          });
          return;
        }

        notification.success({
          message: "Export complete",
          description: `Exported ${result.count} panel user${
            result.count === 1 ? "" : "s"
          } to CSV.`,
        });
      },
      submitOrganization: () => {
        if (totalResponses > 0) {
          setSubmitModalVisible(true);
        }
      },
      assign: () => setAssignModalVisible(true),
    };

    const campaignActions = {
      share: () => handleCampaignShare(),
      delete: () => handleCampaignDelete(),
      edit: () => {
        setEditingCampaign(campaignData?.data);
        setCreateCampaignModalVisible(true);
      },
    };

    const templateActions = {
      view: () => handleTemplateFormView(),
      edit: () => handleTemplateFormEdit(),
      delete: () => setDeleteModalVisible(true),
      share: () => handleShare(),
    };

    const formActions = {
      edit: () => handleEdit(),
      submit: () => handleStartForm(),
      end: () => handleEndForm(),
      copy: () => handleCopyForm(),
      view: () =>
        window.open(
          `${window.location.origin}/admin/question/${formId}/0`,
          "_blank",
        ),
      add: () => {
        if (currentPath === "/admin/campaigns") {
          setCreateCampaignModalVisible(true);
        }
      },
    };

    if (isCampaignsPath && campaignActions[key]) {
      return campaignActions[key]();
    }

    if (isTemplatePath && templateActions[key]) {
      return templateActions[key]();
    }

    const actionToExecute = commonActions[key] || formActions[key];
   
    if (actionToExecute) {
      actionToExecute();
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    setLocalFormData(formData);
  }, [formData]);

  useEffect(() => {
    if (location.pathname === "/admin/forms/archived") {
      setConfig(pageConfigs["/admin/forms/archived"]);
      return;
    }

    if (location.pathname.includes("/questions/details") && questionData) {
      setConfig(getQuestionDetailsConfig(questionData));
    } else if (location.pathname.includes("/organisations/")) {
      setConfig(pageConfigs["organisations-details"]);
    } else if (location.pathname.includes("/interpreter/assignments")) {
      setConfig(pageConfigs["manage-assignments"]);
    } else if (location.pathname.includes("/campaigns/")) {
     const baseConfig = {
        ...pageConfigs["campaign-details"](userRole),
      };

      setConfig(baseConfig);
    } else if (location.pathname.includes("/questions/add")) {
      setConfig(pageConfigs["add-question"]);
    } else if (location.pathname.includes("/questions/edit")) {
      setConfig(pageConfigs["edit-question"]);
    } else if (location.pathname.includes("/questions/details")) {
      setConfig(pageConfigs["question-details"]);
    } else if (
      location.pathname.includes("/forms/") &&
      location.search.includes("tab=responses")
    ) {
      setConfig(pageConfigs["form-responses"](userRole, totalResponses));
    } else if (
      location.pathname.includes("/forms/") &&
      !location.pathname.includes("/questions/")
    ) {
      setConfig({
        ...pageConfigs["form-details"],
        actions: getFormActions(localFormData?.status || formStatus),
      });
    } else if (location.pathname === "/admin/templates") {
      setConfig(pageConfigs["/admin/templates"]);
    } else if (location.pathname.startsWith("/admin/template-form/")) {
      setConfig(pageConfigs["/admin/template-form"]);
    } else {
      setConfig(resolvePageConfig(location.pathname, location.search));
    }
  }, [
    userRole,
    currentPath,
    location.pathname,
    location.search,
    formStatus,
    localFormData,
    questionData,
    getQuestionDetailsConfig,
    totalResponses,
  ]);

  if (location.pathname.includes("/admin/question")) {
    return null;
  }

  if (
    location.pathname === "/admin/video-catalog" ||
    location.pathname === "/admin/dashboard" ||
    location.pathname === "/admin/templates" ||
    location.pathname === "/admin/question-template" ||
    location.pathname === "/interpreter/dashboard" ||
    location.pathname === "/organisation/dashboard"
  ) {
    return (
      <div className="bg-white relative">
        {showToggle && (
          <div className="top-0 h-10 z-40 bg-white w-16 transition-all duration-300">
            <div
              onClick={() => setCollapsed(!collapsed)}
              className="absolute top-4 left-0 p-2 rounded-lg"
            >
              {collapsed ? (
                <MenuUnfoldOutlined className="text-lg" />
              ) : (
                <MenuFoldOutlined className="text-lg" />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white relative flex flex-wrap">
      {showToggle && (
        <div
          className={`top-0 h-10 z-40 bg-white ${
            collapsed ? "w-16" : "w-16"
          } transition-all duration-300`}
        >
          <div
            onClick={() => setCollapsed(!collapsed)}
            className="absolute top-4 left-0 p-2 rounded-lg "
          >
            {collapsed ? (
              <MenuUnfoldOutlined className="text-lg" />
            ) : (
              <MenuFoldOutlined className="text-lg" />
            )}
          </div>
        </div>
      )}
      <div
        className={`transition-all duration-300 w-full ${
          collapsed ? "" : "ml-0"
        }`}
      >
        {isDetailsPage ? (
          <div className="flex flex-col md:flex-row justify-between gap-1 p-2 md:p-6 py-2 pb-0 md:pb-0 w-full">
            <div className="w-full md:w-auto">
              <div className="flex items-center gap-2 text-sm mb-0 flex-wrap">
                <p
                  onClick={handleBack}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  {config?.breadcrumb.parent}
                </p>
                <span className="text-black">{`/`}</span>
                {config?.breadcrumb.middle && (
                  <>
                    <span
                      className="text-gray-400 hover:text-gray-600 cursor-pointer"
                      onClick={() => navigate(`/admin/forms/1`)}
                    >
                      {config?.breadcrumb.middle}
                    </span>
                    <span className="text-black">{`>`}</span>
                  </>
                )}
                <span className="text-black">{config?.breadcrumb.current}</span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3 ">
                  <div
                    onClick={handleBack}
                    className="flex items-center justify-center w-8 h-8 bg-secondary rounded-full hover:bg-gray-100 cursor-pointer"
                  >
                    <LuArrowLeft size={18} />
                  </div>
                  <h1 className="text-xl md:text-2xl font-semibold my-0">
                    {config?.breadcrumb.current}
                  </h1>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {config?.actions?.map((action) =>
                action.type === "button" ? (
                  <button
                    key={action.key}
                    className={action.className + " !px-2"}
                    onClick={() => handleActionClick(action)}
                    disabled={
                      (action.key === "submit" && isStarting) ||
                      (action.key === "end" && isStarting)
                    }
                  >
                    {isStarting &&
                    (action.key === "submit" || action.key === "end") ? (
                      <span className="loading loading-spinner"></span>
                    ) : (
                      action.label
                    )}
                  </button>
                ) : (
                  <button
                    key={action.key}
                    className="w-11 h-11 flex items-center justify-center border-0 rounded-lg bg-secondary hover:bg-gray-100 disabled:opacity-50"
                    onClick={() => handleActionClick(action)}
                    disabled={
                      (action.key === "delete" && isDeleting) ||
                      (action.key === "edit" && isEditing) ||
                      (action.key === "copy" && isCopyingForm)
                    }
                  >
                    {action.icon}
                  </button>
                ),
              )}
            </div>
          </div>
        ) : config?.title ? (
          <>
            <div className="flex flex-col md:flex-row justify-between gap-4 mb-0 m-6 mt-2 pb-2 items-end border-b border-gray-200">
              <div className="flex-1">
                <h1
                  className={`md:text-2l text-xl   font-bold text-gray-900 mb-0  ${
                    !collapsed && ""
                  }`}
                >
                  {config?.title}
                </h1>
                <p className="text-lightBlack font-light text-sm w-[80%] mt-1 mb-0">
                  {config?.description}
                </p>
              </div>

              {config?.actions?.length > 0 && (
                <div className="flex items-center gap-3 flex-wrap">
                  {config?.actions.map((action) => (
                    <Button
                      key={action?.key}
                      type={action?.type}
                      icon={action?.icon}
                      className={
                        action?.type === "primary"
                          ? "btnPrimary rounded-2xl"
                          : "btnSecondary rounded-2xl"
                      }
                      onClick={() => {
                        if (action.key === "export") {
                          setExportModalVisible(true);
                        } else if (action.key === "orgnization") {
                          setAddOrgModalVisible(true);
                        } else if (action.key === "createForm") {
                          setCreateFormModalVisible(true);
                        } else if (action.key === "ExportReportOrganisation") {
                          setExportModalOpen(true);
                        } else {
                          handleActionClick(action);
                        }
                      }}
                    >
                      {action?.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
      <ExportModal
        visible={exportModalVisible}
        onClose={() => setExportModalVisible(false)}
      />
      <AddOrganizationModal
        visible={addOrgModalVisible}
        onClose={() => setAddOrgModalVisible(false)}
      />
      <DeleteModal
        visible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onDelete={handleDelete}
        title="Delete Form"
        message="Are you sure you want to delete this Form?"
        isLoading={isDeleting}
      />
      <CreateFormModal
        visible={createFormModalVisible}
        onClose={() => {
          setCreateFormModalVisible(false);
          setIsEditing(false);
          setEditingForm(null);
        }}
        editData={editingForm}
        onSuccess={() => {
          setCreateFormModalVisible(false);
          setIsEditing(false);
          setEditingForm(null);
        }}
        istemplate={
          location.pathname.startsWith("/admin/template-form/") ? true : false
        }
      />
      <SubmitFormModal
        visible={submitModalVisible}
        onClose={() => setSubmitModalVisible(false)}
        formData={formData}
      />
      <ActivateDateWarningModal
        visible={showActivateDateModal}
        onCancel={() => setShowActivateDateModal(false)}
        onSave={handleConfirmActivationWithDates}
        initialStartDate={toYMD(new Date())}
        initialEndDate={toYMD(new Date())}
      />
      <AssignInterpreterModal
        visible={assignModalVisible}
        questionId={questionId}
        formData={formData}
        organizationId={formData?.organization?._id}
        onClose={() => {
          setAssignModalVisible(false);
        }}
      />
      <AddInterpreterModal
        visible={addInterpreterModalVisible}
        onClose={() => setAddInterpreterModalVisible(false)}
      />
      <AddPanelUserModal
        visible={addPanelUserModalVisible}
        onClose={() => setAddPanelUserModalVisible(false)}
      />
      <ImportPanelUsersModal
        visible={importPanelUsersModalVisible}
        onClose={() => setImportPanelUsersModalVisible(false)}
      />
      <CreateCampaignModal
        visible={createCampaignModalVisible}
        onClose={() => {
          setCreateCampaignModalVisible(false);
          setEditingCampaign(null);
        }}
        editData={editingCampaign}
        onSuccess={handleSuccess}
      />
      <ExportReportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
      />
      <WidgetModal
        visible={widgetModalVisible}
        onClose={() => setWidgetModalVisible(false)}
        formId={formId}
        formTitle={formData?.title}
      />
      <CopyFormEnvironmentModal
        visible={copyFormModalVisible}
        onClose={() => setCopyFormModalVisible(false)}
        onConfirm={handleCopyFormConfirm}
        formTitle={localFormData?.title || formData?.title || ""}
        loading={isCopyingForm}
      />
    </div>
  );
};

export default PageHeader;
